<template>
  <el-form ref="formRef" :model="creativeForm" :rules="getCreativeFormRules.call({ creativeForm, projectForm })" label-width="0">
    <el-form-item v-if="showName" prop="creative_name">
      <el-input
        v-model="creativeForm.creative_name"
        @blur="onCreativeNameInputBlur"
        placeholder="请输入创意组名称"
        style="width: 320px"
      ></el-input>
    </el-form-item>
    <el-form-item prop="selectedMaterials">
      <div class="material-types">
        <el-tag
          effect="light"
          @click="toggleMaterialTag(MATERIAL_TYPE_ID[MATERIAL_TYPE.VIDEO])"
          :type="creativeForm.materialType === MATERIAL_TYPE_ID[MATERIAL_TYPE.VIDEO] ? '' : 'info'"
          size="medium"
        >
          <span class="group-name">视频({{ creativeForm.selectedMaterials.video.length }}{{ isV3 ? '' : `/${maxCount}` }})</span>
        </el-tag>

        <el-tag
          effect="light"
          @click="toggleMaterialTag(MATERIAL_TYPE_ID[MATERIAL_TYPE.IMAGE])"
          :type="creativeForm.materialType === MATERIAL_TYPE_ID[MATERIAL_TYPE.IMAGE] ? '' : 'info'"
          size="medium"
        >
          <span class="group-name">图片({{ creativeForm.selectedMaterials.image.length }}{{ isV3 ? '' : `/${maxCount}` }})</span>
        </el-tag>

        <el-tag
          effect="light"
          @click="toggleMaterialTag(MATERIAL_TYPE_ID[MATERIAL_TYPE.IMAGE_AUDIO])"
          :type="creativeForm.materialType === MATERIAL_TYPE_ID[MATERIAL_TYPE.IMAGE_AUDIO] ? '' : 'info'"
          size="medium"
        >
          <span class="group-name">图文({{ creativeForm.selectedMaterials.imageAudio.length }}{{ isV3 ? '' : '/10' }})</span>
        </el-tag>

        <span v-if="isV3" class="group-name">素材({{ materialTotal }}/{{ maxCount }})</span>

        <el-button type="text" @click="emit('show-material-selector')">+添加素材</el-button>
      </div>
    </el-form-item>

    <el-form-item>
      <div class="selected-materials">
        <div class="selected-videos" v-show="creativeForm.materialType === MATERIAL_TYPE_ID[MATERIAL_TYPE.VIDEO]">
          <VideoMaterial
            v-for="(item, index) in creativeForm.selectedMaterials.video"
            :key="item.id"
            v-bind="{ ...item, index }"
            :cover-replaceable="!disableExistingVideoCoverReplace || !item._stdExisting"
            @remove="onMaterialRemove('video', index)"
            @upload="onVideoReplace(index, $event)"
          >
          </VideoMaterial>
        </div>

        <div class="selected-images" v-show="creativeForm.materialType === MATERIAL_TYPE_ID[MATERIAL_TYPE.IMAGE]">
          <ImageMaterial
            v-for="(item, index) in creativeForm.selectedMaterials.image"
            :key="item.id"
            v-bind="{ ...item, index }"
            @remove="onMaterialRemove('image', index)"
          >
          </ImageMaterial>
        </div>

        <div class="selected-images" v-show="creativeForm.materialType === MATERIAL_TYPE_ID[MATERIAL_TYPE.IMAGE_AUDIO]">
          <ImageMaterial
            :with-desc="false"
            badge="图文"
            v-for="(item, index) in creativeForm.selectedMaterials.imageAudio"
            :key="item.id"
            v-bind="{ ...item, index }"
            @remove="onMaterialRemove('imageAudio', index)"
          >
          </ImageMaterial>
        </div>
      </div>
    </el-form-item>
  </el-form>
</template>

<script setup>
import { ref, computed, unref } from 'vue'
import { MessageBox } from 'element-ui'
import { MATERIAL_TYPE_ID, MATERIAL_TYPE } from '@/components/material-selector/consts.js'
import VideoMaterial from './components/video.vue'
import ImageMaterial from './components/image.vue'
import { getCreativeFormRules } from '../../rules/index.js'

// eslint-disable-next-line
const props = defineProps({
  form: Object,
  projectForm: Object,
  maxCount: {
    type: Number,
    default: 30
  },
  isV3: {
    type: Boolean,
    default: false
  },
  showName: {
    type: Boolean,
    default: true
  },
  confirmExistingRemoval: {
    type: Boolean,
    default: false
  },
  disableExistingVideoCoverReplace: {
    type: Boolean,
    default: false
  },
  skipMaterialValidation: {
    type: Boolean,
    default: false
  }
})
// eslint-disable-next-line
const emit = defineEmits(['update:form', 'change', 'show-material-selector', 'show-image-audio-add-modal'])

const formRef = ref(null)

// 本组件的form修改不能响应式更改外部，而是通过emit('update:form')来修改，或外部调用setForm方法更新内部
// 之所以这么处理，是创意组名称input输入时，输入一下会失去焦点
const creativeForm = ref({ ...props.form })
const materialTotal = computed(() => {
  const { video = [], image = [], imageAudio = [] } = creativeForm.value.selectedMaterials || {}
  return video.length + image.length + imageAudio.length
})

const projectForm = computed(() => props.projectForm)

/**
 * @description: 删除已选素材
 * @param {*} materialType
 * @param {*} index
 */
async function onMaterialRemove(materialType, index) {
  const newForm = unref(creativeForm)
  const material = newForm.selectedMaterials[materialType][index]
  if (props.confirmExistingRemoval && material?._stdExisting) {
    try {
      await MessageBox.confirm('移除后提交将删除媒体侧已有素材，是否继续？', '提示', {
        confirmButtonText: '继续移除',
        cancelButtonText: '取消',
        type: 'warning'
      })
    } catch (e) {
      return
    }
  }
  newForm.selectedMaterials[materialType] = newForm.selectedMaterials[materialType].filter((_, _index) => _index !== index)
  emit('update:form', newForm)
  emit('change', newForm)
}

function onVideoReplace(index, e) {
  if (props.disableExistingVideoCoverReplace && creativeForm.value.selectedMaterials.video[index]?._stdExisting) return
  emit('replace', index, e)
}

function onCreativeNameInputBlur() {
  emit('update:form', creativeForm.value)
  emit('change', creativeForm.value)
}

function toggleMaterialTag(e) {
  creativeForm.value.materialType = e
  emit('update:form', creativeForm.value)
  emit('change', creativeForm.value)
}

// eslint-disable-next-line
defineExpose({
  setForm: (form) => (creativeForm.value = form),
  getForm: () => unref(creativeForm),
  validate: () => (props.skipMaterialValidation ? Promise.resolve() : formRef.value.validate())
})
</script>

<style lang="scss" scoped>
.material-types {
  .el-tag {
    margin-right: 10px;
    cursor: pointer;
  }
}

.selected-materials {
  overflow-x: hidden;
  padding-top: 12px;
  .selected-images,
  .selected-videos {
    margin-bottom: 20px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    position: relative;
  }

  .selected-videos {
    > div {
      width: 572px;
      min-width: 572px;
    }
  }

  .selected-images {
    > div {
      width: calc((25% - 8px));
      min-width: calc((25% - 8px));
    }
  }
}
</style>
